"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Cpu, Globe, MessageSquareCode, ChevronRight, Terminal, Zap, Database } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  title: "Tech Strategy",
  description: "Developing tailored roadmaps that align agentic AI solutions with your long-term business objectives and competitive landscape.",
  icon: Globe,
  logs: ["STRATEGY_MAP_v2.0", "ALIGNMENT_SCORE: 98%", "SCALING_CAPACITY: HIGH", "MODELS_IDENTIFIED"],
  link: "/ConsultingServicesPage"
}, {
  title: "Process Optimization",
  description: "Re-engineering core operations using automated workflows to eliminate bottlenecks and maximize organizational efficiency.",
  icon: Cpu,
  logs: ["WORKFLOW_AUDIT_INIT", "BOTTLENECK_DETECTED: 0", "PROCESS_SYNC: ACTIVE", "EFFICIENCY_GAINS: 40%"],
  link: "/ConsultingServicesPage"
}, {
  title: "Change Management",
  description: "Guiding your workforce through digital transition with proven frameworks that ensure high adoption and cultural alignment.",
  icon: MessageSquareCode,
  logs: ["ADOPTION_METRICS", "CULTURAL_SHIFT: POSITIVE", "TRAINING_COMPLETED", "ENTERPRISE_READY"],
  link: "/ConsultingServicesPage"
}];
export default function OperationalIntelligence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="5366" id="our-transformative-capabilities" ref={ref} className={`bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[128px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-6 text-foreground">
            Transformative <span className="text-secondary">Capabilities</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">Built for custom manufacturers, HVAC &amp; plumbing contractors, health professionals, and growing professional service firms.&nbsp; Leveraging decades of global consulting experience to guide SMBs through the next generation of AI-driven evolution and sustainable process optimization.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((pillar, i) => <div key={i} data-index={i} className="group h-full">
              <Card className="h-full bg-card/50 border-border/50 backdrop-blur-sm relative overflow-hidden transition-all duration-500 hover:border-secondary/50 hover:shadow-[0_0_30px_-10px_var(--color-secondary)] hover:-translate-y-1">
                
                {}
                <div className="absolute inset-0 bg-primary/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center px-8 pointer-events-none z-0">
                  <div className="font-mono text-xs text-secondary/70 space-y-2">
                    {pillar.logs.map((log, j) => <div key={j} className="animate-pulse" style={{
                  animationDelay: `${j * 150}ms`
                }}>
                        <span className="text-secondary mr-2">{">"}</span>
                        {log}
                      </div>)}
                    <div className="h-4 w-2 bg-secondary animate-pulse mt-2" />
                  </div>
                </div>

                <CardHeader className="relative z-10 pb-2">
                  <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-secondary/10 to-transparent border border-secondary/20 shadow-[0_0_15px_-3px_var(--color-secondary)] group-hover:scale-110 transition-transform duration-500">
                    <pillar.icon className="w-7 h-7 text-secondary drop-shadow-[0_0_8px_rgba(37,99,235,0.5)]" />
                  </div>
                  <CardTitle className="text-xl font-bold tracking-tight text-foreground group-hover:text-secondary transition-colors">
                    {pillar.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="relative z-10">
                  <CardDescription className="text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors">
                    {pillar.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="relative z-10 pt-4">
                  <Link to={pillar.link} className="inline-flex items-center text-sm font-medium text-secondary hover:text-secondary/80 transition-colors group/link">
                    Learn More
                    <ChevronRight className="ml-1 w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>
                </CardFooter>

                {}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-secondary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-secondary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}